import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Sparkles } from "lucide-react";
import { OnboardingFlow } from "@/components/OnboardingFlow";

const Onboarding = () => {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleComplete = async () => {
    if (!name.trim()) {
      toast({
        title: "Nome necessário",
        description: "Por favor, nos diga como prefere ser chamado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ name: name.trim() })
        .eq("id", user?.id);

      if (error) throw error;

      toast({
        title: "Bem-vindo à VYRA! ✨",
        description: `Prazer em te conhecer, ${name}!`,
      });
      
      // Mostrar OnboardingFlow antes de ir para /today
      setShowOnboardingFlow(true);
    } catch (error: any) {
      toast({
        title: "Erro ao salvar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showOnboardingFlow && (
        <OnboardingFlow onComplete={() => navigate("/today")} />
      )}
      
      {!showOnboardingFlow && (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-md p-8 text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            Bem-vindo à VYRA
          </h1>
          <p className="text-muted-foreground">
            Um espaço acolhedor para organizar sua vida sem pressão.
            Estamos aqui para te apoiar, não cobrar.
          </p>
        </div>

        <div className="space-y-6">
          <div className="text-left">
            <label htmlFor="name" className="text-sm font-medium mb-2 block">
              Como prefere ser chamado?
            </label>
            <Input
              id="name"
              type="text"
              placeholder="Seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleComplete()}
              autoFocus
              className="text-center text-lg"
            />
          </div>

          <Button 
            onClick={handleComplete} 
            disabled={loading || !name.trim()}
            className="w-full"
            size="lg"
          >
            {loading ? "Salvando..." : "Começar jornada"}
          </Button>
        </div>

        <div className="mt-8 p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            💡 <strong>Dica:</strong> A VYRA aprende com você. 
            Quanto mais você usa, mais personalizada ela fica.
          </p>
        </div>
      </Card>
    </div>
      )}
    </>
  );
};

export default Onboarding;